import React from "react";
import FloraImage from "../assets/redesign/showcase/platform-flora.png";
import FloraHoverImage from "../assets/redesign/showcase/platform-flora-hover.png";
import HwaImage from "../assets/redesign/showcase/platform-hwa.png";
import HwaHoverImage from "../assets/redesign/showcase/platform-hwa-hover.png";
import ItdbImage from "../assets/redesign/showcase/platform-itdb.png";
import ItdbHoverImage from "../assets/redesign/showcase/platform-itdb-hover.png";
import MarketImage from "../assets/redesign/showcase/platform-market.png";
import MarketHoverImage from "../assets/redesign/showcase/platform-market-hover.png";
import PodcastImage from "../assets/redesign/showcase/platform-podcast.png";
import PodcastHoverImage from "../assets/redesign/showcase/platform-podcast-hover.png";
import ProfileImage from "../assets/redesign/showcase/platform-profile.png";
import ProfileHoverImage from "../assets/redesign/showcase/platform-profile-hover.png";
import { ReactComponent as ShowcaseArrow } from "../assets/redesign/showcase/showcase-arrow.svg";

type Showcase = {
  id: "itdb" | "hwa" | "profile" | "podcast" | "market" | "flora";
  title: string;
  headline: string;
  description: React.ReactNode;
  image: string;
  hoverImage: string;
  link?: string;
  badge?: string;
  comingSoon?: boolean;
};

const showcases: Showcase[] = [
  {
    id: "itdb",
    title: "International Theatre Database(ITDb)",
    headline: "The World’s Largest Global Performing Arts Database",
    description:
      "Our mission is to provide authoritative, verified data on performing arts worldwide.",
    image: ItdbImage,
    hoverImage: ItdbHoverImage,
    link: "https://internationaltheatredatabase.com/",
  },
  {
    id: "hwa",
    title: "HereWeAre",
    headline: "The app for performing arts lovers worldwide.",
    description:
      "Explore, connect, and document with Artist Lounge, Logs, SpotChat, and more—all in the palm of your hand.",
    image: HwaImage,
    hoverImage: HwaHoverImage,
  },
  {
    id: "profile",
    title: "ITDb Profile",
    headline: "A Professional Profile for Performing Arts Creators",
    description:
      "Connect your career to a wider world with ITDb. Easily manage and share your professional journey.",
    image: ProfileImage,
    hoverImage: ProfileHoverImage,
    link: "https://profile.hereweare.io/",
    badge: "Beta",
  },
  {
    id: "podcast",
    title: "HereWeAre Podcast",
    headline: "Unfiltered, Industry-Led Dialogue",
    description:
      "No rehearsal. No script. No editing. Just raw, one-take conversations between creators.",
    image: PodcastImage,
    hoverImage: PodcastHoverImage,
    link: "https://www.youtube.com/playlist?list=PLNMB2CeexbEEudjOn1FfEFGnw4xSa515Y",
  },
  {
    id: "market",
    title: "ITDb Market",
    headline: "A Global Marketplace for Productions",
    description:
      "Connect your career to a wider world with ITDb. Easily manage and share your professional journey.",
    image: MarketImage,
    hoverImage: MarketHoverImage,
    comingSoon: true,
  },
  {
    id: "flora",
    title: "Flora Awards",
    headline: "Preserving theatre legacy. Honouring stage excellence.",
    description:
      "Celebrating exceptional talent and providing vital support to artists on their creative journeys.",
    image: FloraImage,
    hoverImage: FloraHoverImage,
    link: "https://thefloraawards.com/",
  },
];

function ShowcaseCard({ card, onInstall }: { card: Showcase; onInstall: () => void }) {
  const content = (
    <>
      <div className="showcase-card__visual">
        <img
          className="showcase-card__image showcase-card__image--base"
          src={card.image}
          alt=""
        />
        <img
          className="showcase-card__image showcase-card__image--hover"
          src={card.hoverImage}
          alt=""
        />
        <span className="showcase-card__more" aria-hidden="true">
          <ShowcaseArrow />
        </span>
      </div>
      <div className="showcase-card__copy">
        <div className="showcase-card__title">
          <h3>{card.title}</h3>
          {card.badge && <span>{card.badge}</span>}
        </div>
        <div className="showcase-card__description">
          <strong>{card.headline}</strong>
          <p>{card.description}</p>
        </div>
        {card.comingSoon && <em>Coming Soon!</em>}
      </div>
    </>
  );

  const className = `showcase-card showcase-card--${card.id}`;
  if (card.id === "hwa") {
    return <button type="button" className={`${className} showcase-card--action`} onClick={onInstall}>{content}</button>;
  }

  return card.link ? (
    <a className={className} href={card.link} target="_blank" rel="noreferrer">
      {content}
    </a>
  ) : (
    <article className={className}>{content}</article>
  );
}

export default function PlatformSection({ onInstall }: { onInstall: () => void }) {
  const leftColumn = showcases.filter((_, index) => index % 2 === 0);
  const rightColumn = showcases.filter((_, index) => index % 2 === 1);

  return (
    <section className="showcase-grid">
      <div className="showcase-grid__column">
        {leftColumn.map((card) => (
          <ShowcaseCard key={card.id} card={card} onInstall={onInstall} />
        ))}
      </div>
      <div className="showcase-grid__column showcase-grid__column--offset">
        {rightColumn.map((card) => (
          <ShowcaseCard key={card.id} card={card} onInstall={onInstall} />
        ))}
      </div>
    </section>
  );
}
